import React from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    TextField,
    Typography,
} from "@mui/material";
import {
    Diversity3,
    Groups2,
    MailOutline,
    Public,
    Savings,
    StarRate,
    StarBorder,
    SupportAgent,
    Update,
    Verified,
} from "@mui/icons-material";
import { useI18n } from "../../../i18n/I18nContext";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const iconMap = {
    Diversity3,
    Groups2,
    HotelClass: StarRate,
    MailOutline,
    Public,
    Savings,
    StarBorder,
    SupportAgent,
    Update,
    Verified,
};

const copy = {
    de: {
        headline: "Ihre Vorteile bei HRI",
        newsletterTitle: "Bleiben Sie informiert",
        newsletterText: "Erhalten Sie die besten Angebote und Neuigkeiten direkt in Ihr Postfach.",
        email: "Ihre E-Mail-Adresse",
        submit: "Abonnieren",
        consent: "Ich willige ein, dass HRI meine E-Mail-Adresse zum Versand des Newsletters verarbeitet. Ich kann die Einwilligung jederzeit widerrufen.",
        success: "Danke, Ihre Newsletter-Anmeldung wurde gespeichert.",
    },
    en: {
        headline: "Your Benefits With HRI",
        newsletterTitle: "Stay informed",
        newsletterText: "Receive the best offers and news directly in your inbox.",
        email: "Your email address",
        submit: "Subscribe",
        consent: "I consent to HRI processing my email address to send the newsletter. I can withdraw my consent at any time.",
        success: "Thank you, your newsletter subscription has been saved.",
    },
    fr: {
        headline: "Vos avantages avec HRI",
        newsletterTitle: "Restez informé",
        newsletterText: "Recevez les meilleures offres et nouveautés directement dans votre boîte de réception.",
        email: "Votre adresse e-mail",
        submit: "S'abonner",
        consent: "J'autorise HRI à traiter mon adresse e-mail pour l'envoi de la newsletter. Je peux retirer mon consentement à tout moment.",
        success: "Merci, votre inscription à la newsletter a été enregistrée.",
    },
    it: {
        headline: "I vostri vantaggi con HRI",
        newsletterTitle: "Restate informati",
        newsletterText: "Ricevete le migliori offerte e novità direttamente nella vostra casella di posta.",
        email: "Il vostro indirizzo e-mail",
        submit: "Iscriviti",
        consent: "Acconsento al trattamento del mio indirizzo e-mail da parte di HRI per l'invio della newsletter. Posso revocare il consenso in qualsiasi momento.",
        success: "Grazie, la vostra iscrizione alla newsletter è stata salvata.",
    },
};

function localized(row, field, lang) {
    return row?.[`${field}_${lang}`] || row?.[`${field}_de`] || "";
}

function BenefitIcon({ name }) {
    const Icon = iconMap[name] || StarBorder;
    return <Icon sx={{ color: "var(--domain-primary)", fontSize: 25 }} />;
}

export default function HomeHriBenefits() {
    const { lang } = useI18n();
    const texts = copy[lang] || copy.de;
    const [benefits, setBenefits] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [email, setEmail] = React.useState("");
    const [consent, setConsent] = React.useState(false);
    const [status, setStatus] = React.useState({ type: "", message: "" });
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        let active = true;
        axios
            .get(`${API_BASE}/api/hri-benefits`, {
                params: { include_inactive: false },
                withCredentials: true,
            })
            .then((res) => {
                if (active) setBenefits(Array.isArray(res.data) ? res.data.slice(0, 4) : []);
            })
            .catch(() => {
                if (active) setBenefits([]);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: "", message: "" });
        setSubmitting(true);
        try {
            await axios.post(
                `${API_BASE}/api/newsletter/subscribe`,
                {
                    email,
                    language: lang || "de",
                    consent,
                    consent_text: texts.consent,
                },
                { withCredentials: true }
            );
            setEmail("");
            setConsent(false);
            setStatus({ type: "success", message: texts.success });
        } catch (err) {
            setStatus({
                type: "error",
                message: err?.response?.data?.error || "Newsletter-Anmeldung fehlgeschlagen.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (!loading && benefits.length === 0) return null;

    return (
        <Box
            sx={{
                position: "relative",
                left: "50%",
                right: "50%",
                width: "100vw",
                mx: "-50vw",
                bgcolor: "var(--domain-background)",
                borderTop: "1px solid #e5e7eb",
                borderBottom: "1px solid #dfe4eb",
                py: { xs: 4, md: 5.5 },
            }}
        >
            <Box sx={{ maxWidth: 1540, mx: "auto", px: { xs: 2, md: 4 } }}>
                <Typography component="h2" sx={{ color: "var(--domain-text)", fontSize: { xs: "1.3rem", md: "1.55rem" }, fontWeight: 800, mb: 2.8 }}>
                    {texts.headline}
                </Typography>

                {loading ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                        <CircularProgress sx={{ color: "var(--domain-primary)" }} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "repeat(4, minmax(0, 1fr))",
                            },
                            gap: 2.5,
                            mb: { xs: 3, md: 4 },
                        }}
                    >
                        {benefits.map((benefit) => (
                            <Box
                                key={benefit.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.8,
                                    minHeight: 92,
                                    p: 2.2,
                                    bgcolor: "background.paper",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 2,
                                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 46,
                                        height: 46,
                                        flex: "0 0 auto",
                                        borderRadius: 2,
                                        display: "grid",
                                        placeItems: "center",
                                        bgcolor: "var(--domain-primary-light)",
                                    }}
                                >
                                    <BenefitIcon name={benefit.icon_name} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ color: "var(--domain-text)", fontSize: "0.98rem", fontWeight: 800, lineHeight: 1.25 }}>
                                        {localized(benefit, "title", lang)}
                                    </Typography>
                                    <Typography sx={{ color: "var(--domain-text-secondary)", fontSize: "0.86rem", lineHeight: 1.35, mt: 0.35 }}>
                                        {localized(benefit, "text", lang)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1.25fr 1fr" },
                        gap: { xs: 2.5, md: 4 },
                        alignItems: "center",
                        bgcolor: "var(--domain-text)",
                        color: "#fff",
                        borderRadius: 2,
                        px: { xs: 2.3, md: 3.2 },
                        py: { xs: 2.5, md: 3 },
                        boxShadow: "0 16px 36px rgba(7, 52, 111, 0.18)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 68, height: 68, borderRadius: "50%", bgcolor: "background.paper", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
                            <MailOutline sx={{ color: "var(--domain-text)", fontSize: 34 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, lineHeight: 1.2 }}>{texts.newsletterTitle}</Typography>
                            <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.82)", mt: 0.7 }}>{texts.newsletterText}</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: "grid", gap: 1 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 1.2 }}>
                            <TextField
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={texts.email}
                                type="email"
                                required
                                size="small"
                                sx={{
                                    bgcolor: "background.paper",
                                    borderRadius: 1,
                                    "& .MuiOutlinedInput-root": { borderRadius: 1 },
                                }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={submitting}
                                sx={{
                                    minWidth: 160,
                                    bgcolor: "var(--domain-primary)",
                                    fontWeight: 800,
                                    textTransform: "none",
                                    "&:hover": { bgcolor: "var(--domain-primary-dark)" },
                                }}
                            >
                                {submitting ? "..." : texts.submit}
                            </Button>
                        </Box>
                        <FormControlLabel
                            sx={{
                                alignItems: "flex-start",
                                m: 0,
                                color: "rgba(255,255,255,0.78)",
                                "& .MuiFormControlLabel-label": { fontSize: "0.76rem", lineHeight: 1.35 },
                            }}
                            control={
                                <Checkbox
                                    required
                                    checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)}
                                    sx={{ color: "rgba(255,255,255,0.7)", p: 0, pr: 1, "&.Mui-checked": { color: "var(--domain-primary)" } }}
                                />
                            }
                            label={texts.consent}
                        />
                        {status.message && <Alert severity={status.type || "info"} sx={{ mt: 0.5 }}>{status.message}</Alert>}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
